// 功能說明（tooltip）：滑鼠停留、鍵盤聚焦會顯示；觸控裝置沒有 hover，點一下也能開關。
// Esc 或點擊外部關閉。自行實作而不用 UI 套件：定位需求單純，省下約 25KB（gzip）的前端 bundle。
import {
  cloneElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n/zh-TW.ts';
import { ICON, Info } from './icons/index.ts';

const OPEN_DELAY = 250;
const CLOSE_DELAY = 120;
const GAP = 8;
const EDGE = 12;

interface HintProps {
  content: ReactNode;
  /** 自訂觸發元素（例如標籤按鈕） */
  children?: ReactElement<{ ref?: Ref<HTMLElement> } & Record<string, unknown>>;
  /** 標題文字本身當觸發點（虛線底線，GDO-22）；children 與 text 都沒給時才顯示資訊圖示按鈕 */
  text?: ReactNode;
  label?: string;
}

interface Position {
  top: number;
  left: number;
  arrowLeft: number;
  placement: 'top' | 'bottom';
}

/** 預設顯示在下方；下方空間不夠就翻到上方；水平方向夾在視窗內 */
export function computePosition(
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>,
  tip: { width: number; height: number },
  viewport: { width: number; height: number },
): Position {
  const below = trigger.bottom + GAP + tip.height <= viewport.height - EDGE;
  const placement = below || trigger.top - GAP - tip.height < EDGE ? 'bottom' : 'top';
  const top = placement === 'bottom' ? trigger.bottom + GAP : trigger.top - GAP - tip.height;
  const center = trigger.left + trigger.width / 2;
  const left = Math.min(Math.max(center - tip.width / 2, EDGE), Math.max(EDGE, viewport.width - EDGE - tip.width));
  return { top, left, placement, arrowLeft: Math.min(Math.max(center - left, 10), tip.width - 10) };
}

export function Hint({ content, children, text, label = t.hint.trigger }: HintProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const schedule = useCallback((next: boolean, delay: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(next), delay);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const trigger = triggerRef.current;
      const tip = tipRef.current;
      if (!trigger || !tip) return;
      setPos(
        computePosition(
          trigger.getBoundingClientRect(),
          { width: tip.offsetWidth, height: tip.offsetHeight },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { passive: true, capture: true });
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, { capture: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Esc 只關閉說明：在 window 的捕獲階段先處理並停止傳遞，外層的面板、詳情、導覽不會跟著關閉
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !tipRef.current?.contains(target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const triggerProps = {
    ref: triggerRef,
    'aria-describedby': open ? id : undefined,
    onMouseEnter: () => schedule(true, OPEN_DELAY),
    onMouseLeave: () => schedule(false, CLOSE_DELAY),
    onFocus: () => schedule(true, 0),
    onBlur: () => schedule(false, 0),
    onClick: (e: MouseEvent) => {
      // 觸發點本身是操作按鈕（有自己的 onClick）時執行操作；說明仍可用滑鼠移上或鍵盤聚焦查看
      const own = children?.props.onClick;
      if (typeof own === 'function') {
        own(e);
        return;
      }
      clearTimeout(timer.current);
      setOpen((o) => !o);
    },
  };

  const trigger = children ? (
    cloneElement(children, triggerProps)
  ) : text !== undefined ? (
    <button type="button" className="hint-text" {...triggerProps} ref={triggerRef as Ref<HTMLButtonElement>}>
      {text}
    </button>
  ) : (
    <button
      type="button"
      className="hint-trigger"
      aria-label={label}
      {...triggerProps}
      ref={triggerRef as Ref<HTMLButtonElement>}
    >
      <Info {...ICON} />
    </button>
  );

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className={`hint hint-${pos?.placement ?? 'bottom'}`}
            style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
            onMouseEnter={() => clearTimeout(timer.current)}
            onMouseLeave={() => schedule(false, CLOSE_DELAY)}
          >
            {content}
            <span className="hint-arrow" style={{ left: pos?.arrowLeft ?? 0 }} aria-hidden="true" />
          </div>,
          document.body,
        )}
    </>
  );
}
